import { BsIntersect } from '@react-icons/all-files/bs/BsIntersect'
import { IoEllipsisHorizontal } from '@react-icons/all-files/io5/IoEllipsisHorizontal'
import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import type { ColumnDef } from '@tanstack/react-table'
import React, { useMemo } from 'react'
import type { NavigateFunction } from 'react-router-dom'

import {
  SEARCH_APPEARS_ON_COLUMN_FIELD,
  SEARCH_APPEARS_ON_FILTER_FIELD,
  SEARCH_INDICATORS_COLUMN_FIELD,
  SEARCH_SOURCE_COLUMN_FIELD,
  SOURCE_SCOPE,
} from '@disco/constants/search'
import {
  selectIsMdsSearchEnabled,
  startTrackSimilaritySearch,
} from '@disco/state-management/reducers/search-page'
import { getSearchMetadataKeys } from '@disco/state-management/selectors/search'

import type { FilterDefinition, MetadataDef } from 'types/filter-page'
import type { SearchType } from 'types/search-page'
import type { Track } from 'types/track'

import type { Row } from 'components/ds/DataTable'
import { RowAction } from 'components/ds/DataTable'
import { createColumn } from 'components/search/search-columns'
import {
  defaultColumnSorting,
  getColumnTypes,
} from 'components/search/search-columns/config'
import { discoDefaultMetadata } from 'components/search/search-filters/config'
import { SearchResultsSource } from 'components/search/search-results-source'

import { useSelector } from 'hooks/use-redux'

import type { AppDispatch } from 'store/configureStore'

import { useAppearsOnEnabled } from './use-appears-on'
import { useIndicatorsEnabled } from './use-indicators'

const discoColumns: MetadataDef[] = [
  {
    field: 'Duration',
    label: 'Duration',
    type: 'number',
  },
  {
    field: 'Waveform',
    label: 'Waveform',
    type: 'number',
  },
  {
    field: 'NativeTags',
    label: 'Tags',
    type: 'number',
  },
  {
    field: 'id',
    label: 'ID',
    type: 'text',
  },
]

const sourceColumn: MetadataDef = {
  field: SEARCH_SOURCE_COLUMN_FIELD,
  label: 'Source',
  type: 'text',
}

const appearsOnColumn: MetadataDef = {
  field: SEARCH_APPEARS_ON_COLUMN_FIELD,
  label: 'Appears on',
  type: 'text',
}

const indicatorsColumn: MetadataDef = {
  field: SEARCH_INDICATORS_COLUMN_FIELD,
  label: 'Indicators',
  type: 'text',
}

export const useAvailableMetadata = (): MetadataDef[] => {
  const searchMetadataKeys = useSelector(getSearchMetadataKeys)
  return useMemo(
    () =>
      searchMetadataKeys
        .slice()
        .sort((a, b) => a.text.localeCompare(b.text))
        .map(item => ({
          label: item.text,
          field: item.key,
          type: item.type,
          values: item.values,
        })),
    [searchMetadataKeys],
  )
}

export const useFiltersDefinition = (): FilterDefinition[] => {
  const availableMetadata = useAvailableMetadata()
  const appearsOnEnabled = useAppearsOnEnabled()

  const definition = useMemo<FilterDefinition[]>(
    () => [
      ...(appearsOnEnabled
        ? [
            {
              label: 'Appears on',
              type: 'number' as const,
              field: SEARCH_APPEARS_ON_FILTER_FIELD,
              isNew: true,
            },
          ]
        : []),
      {
        label: 'Title',
        type: 'text',
        field: 'Title',
        fixed: true,
      },
      {
        label: 'Artist',
        type: 'text',
        field: 'Artist',
        fixed: true,
      },
      {
        label: 'Year',
        type: 'year',
        field: 'Year',
        fixed: true,
      },

      {
        label: 'Metadata',
        options: availableMetadata.filter(item =>
          discoDefaultMetadata.includes(item.field),
        ),
      },
      {
        label: 'Custom fields',
        options: availableMetadata.filter(item => {
          if (discoDefaultMetadata.includes(item.field)) {
            return false
          }
          // For dropdown fields, exclude if they have no values/options
          if (
            item.type === 'dropdown_single' ||
            item.type === 'dropdown_multi'
          ) {
            return (
              item.values &&
              Array.isArray(item.values) &&
              item.values.length > 0
            )
          }

          // Include all other custom field types
          return true
        }),
      },

      {
        label: 'Tags',
        type: 'tags',
        field: 'track_tags',
      },
      {
        label: 'Look in',
        options: [
          {
            label: 'Inboxes',
            type: 'group',
            field: 'look_in_inboxes',
          },
          {
            label: 'Channels',
            type: 'group',
            field: 'look_in_channels',
          },
        ],
      },
      {
        label: 'Date ingested',
        type: 'date',
        field: 'date',
      },
      {
        label: 'Duration',
        type: 'duration',
        field: 'duration',
      },
    ],
    [availableMetadata, appearsOnEnabled],
  )

  return definition
}

export const useSearchTableColumns = () => {
  const availableMetadata = useAvailableMetadata()
  const mdsEnabled = useSelector(selectIsMdsSearchEnabled)
  const appearsOnEnabled = useAppearsOnEnabled()
  const indicatorsEnabled = useIndicatorsEnabled()

  const columns = useMemo<ColumnDef<Track>[]>(() => {
    const columnTypes = getColumnTypes(mdsEnabled)
    return [
      // A business custom field can share a reserved display-column name, which
      // would produce two columns with the same id.
      ...availableMetadata.filter(
        item =>
          item.field !== SEARCH_SOURCE_COLUMN_FIELD &&
          !(indicatorsEnabled && item.field === SEARCH_INDICATORS_COLUMN_FIELD),
      ),
      ...discoColumns,
      ...(mdsEnabled ? [sourceColumn] : []),
      ...(appearsOnEnabled ? [appearsOnColumn] : []),
      ...(indicatorsEnabled ? [indicatorsColumn] : []),
    ]
      .sort((a, b) => {
        const indexA = defaultColumnSorting.indexOf(a.field)
        const indexB = defaultColumnSorting.indexOf(b.field)

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }
        if (indexA !== -1) {
          return -1
        }
        if (indexB !== -1) {
          return 1
        }

        return a.label.localeCompare(b.label)
      })
      .map(item => createColumn(item, columnTypes))
  }, [availableMetadata, mdsEnabled, appearsOnEnabled, indicatorsEnabled])

  return columns
}

export const useMdsRowSelectionCondition = ():
  | ((_row: Row<Track>) => boolean)
  | undefined => {
  const mdsEnabled = useSelector(selectIsMdsSearchEnabled)
  return useMemo(
    () =>
      mdsEnabled
        ? (row: Row<Track>) => row.original.scope !== SOURCE_SCOPE.MDS
        : undefined,
    [mdsEnabled],
  )
}

const SimilaritySearchIcon = () => (
  <BsIntersect size={16} color="var(--gray-11)" />
)

export const getTrackActions = (
  row: Track,
  navigate: NavigateFunction,
  dispatch: AppDispatch,
  simSearchEnabled: boolean,
) => {
  const actions: RowAction[] = [
    ...(simSearchEnabled
      ? [
          {
            label: 'Find similar',
            tooltip: 'Find similar',
            icon: <SimilaritySearchIcon />,
            onClick: () => {
              // Reuses the paste-a-link similarity flow with the track's signed
              // stream_url as the query. Works for MDS rows too — the signature
              // carries the cross-business authorization.
              dispatch(startTrackSimilaritySearch(row))
            },
          },
        ]
      : []),
    {
      label: 'Info',
      icon: <RxInfoCircled size={16} color="var(--gray-11)" />,
      onClick: () => {
        navigate(`/search/track/${row.id}/edit-metadata`)
      },
    },
    {
      label: 'Add to',
      icon: <LuPlus size={16} color="var(--gray-11)" />,
      subActions: [
        {
          label: 'Playlist',
          onClick: () => {
            navigate(`/search/track/${row.id}/add-to-playlist`)
          },
        },
        {
          label: 'Channel',
          onClick: () => {
            navigate(`/search/track/${row.id}/add-to-channel`)
          },
        },
        {
          label: 'Playlist Creator',
          onClick: () => {
            navigate(`/search/track/${row.id}/add-to-playlist-creator`)
          },
        },
      ],
    },
    {
      label: 'More',
      icon: <IoEllipsisHorizontal size={16} color="var(--gray-11)" />,
      subActions: [
        {
          label: 'Stats',
          onClick: () => {
            navigate(`/search/track/${row.id}/stats`)
          },
        },
      ],
    },
  ]

  return actions
}

export const getPlaceholderForSearchType = (searchType: SearchType): string => {
  switch (searchType) {
    case 'keyword':
      return 'Search by keywords or metadata'
    case 'similarity':
      return 'Paste a DISCO, YouTube, Spotify or Apple Music track link'
    case 'prompt':
      return "Try a prompt, like 'upbeat indie tracks for a road trip'"
    default:
      return 'Search by keywords or metadata'
  }
}

export const getSearchResultsSource = (
  searchType: SearchType,
  aimsEnabled: boolean,
): React.ReactNode | null => {
  if (searchType === 'keyword' || !aimsEnabled) return null
  return <SearchResultsSource />
}
